import { createContext, useCallback, useState } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { useToastNotifications } from '@nbirl/nbi-common-ui-libs';

import { useContextFallback, useErrorToast } from '../hooks';
import { DownloadFilesRequest, DownloadStatusResponse } from '../models';
import {
  useDownloadDocumentMutation,
  useDownloadFilesMutation,
  useDownloadStatusMutation,
} from '../queries';

type DownloadContextType = {
  download: (request: DownloadFilesRequest) => void;
  isDownloading: boolean;
};

const DownloadContext = createContext<DownloadContextType | undefined>(
  undefined
);
DownloadContext.displayName = 'DownloadContext';

type DownloadStatusProcess = {
  processId: string;
};

export const DownloadProvider: React.FC = ({ children }) => {
  const [downloadStatusProcess, setDownloadStatusProcess] =
    useState<DownloadStatusProcess>({ processId: '' });
  const { addToast, removeToast } = useToastNotifications();
  const { showErrorToast } = useErrorToast();
  const downloadDocumentMutation = useDownloadDocumentMutation();
  const downloadFilesMutation = useDownloadFilesMutation();
  const downloadStatusMutation = useDownloadStatusMutation();

  const showDownloadErrorToast = useCallback(() => {
    showErrorToast(
      'Selected files and folders failed to download. Please try again.'
    );
  }, [showErrorToast]);

  const initBrowserDownload = useCallback((downloadUrl: string) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    link.remove();
  }, []);

  const downloadDocument = useCallback(
    (documentId: string) => {
      downloadDocumentMutation.mutate(
        { documentId },
        {
          onSuccess: ({ downloadUrl }) => initBrowserDownload(downloadUrl),
          onError: () => showDownloadErrorToast(),
        }
      );
    },
    [downloadDocumentMutation, initBrowserDownload, showDownloadErrorToast]
  );

  const getDownloadStatusQuery = (
    process: DownloadStatusProcess
  ): UseQueryOptions<DownloadStatusResponse> => {
    const { processId } = process;
    const handleError = () => {
      setDownloadStatusProcess({ processId: '' });
      removeToast(processId);
      showDownloadErrorToast();
    };

    return {
      queryKey: ['download-files-status', processId],
      enabled: !!processId,
      refetchInterval: 2000,
      queryFn: async () => downloadStatusMutation.mutateAsync({ processId }),
      onSuccess: ({ status, downloadUrl }) => {
        switch (status) {
          case 'STARTED':
            break;
          case 'FINISHED':
            setDownloadStatusProcess({ processId: '' });
            removeToast(processId);
            initBrowserDownload(downloadUrl);
            break;
          case 'FAILED':
            handleError();
            break;
        }
      },
      onError: handleError,
    };
  };

  const downloadFiles = useCallback(
    async ({ documentIds, folderIds }: DownloadFilesRequest) => {
      try {
        const { downloadProcessId } = await downloadFilesMutation.mutateAsync({
          documentIds,
          folderIds,
        });
        setDownloadStatusProcess({ processId: downloadProcessId });

        addToast({
          id: downloadProcessId,
          variant: 'info',
          text: 'Generating ZIP for selected files and folders',
          autoRemove: false,
        });
      } catch (error) {
        showDownloadErrorToast();
        throw error;
      }
    },
    [addToast, downloadFilesMutation, showDownloadErrorToast]
  );

  useQuery(getDownloadStatusQuery(downloadStatusProcess));

  const isDownloading =
    downloadDocumentMutation.isLoading ||
    downloadFilesMutation.isLoading ||
    downloadStatusMutation.isLoading;

  const download = useCallback(
    ({ documentIds, folderIds }: DownloadFilesRequest) => {
      if (
        isDownloading ||
        (documentIds.length === 0 && folderIds.length === 0)
      ) {
        return;
      }
      if (documentIds.length > 1 || folderIds.length > 0) {
        downloadFiles({ documentIds, folderIds });
      } else {
        downloadDocument(documentIds[0]);
      }
    },
    [downloadDocument, downloadFiles, isDownloading]
  );

  return (
    <DownloadContext.Provider
      value={{
        download,
        isDownloading,
      }}
    >
      {children}
    </DownloadContext.Provider>
  );
};

export const useDownload = () => useContextFallback(DownloadContext);
